import { useDraggable } from "@dnd-kit/core";
import { X, Shield, Star } from "lucide-react";
import type { TeamPositionEntryPublic, PositionDef } from "@dugads/shared";

export interface PositionCardProps {
  jerseyNumber: number;
  position: PositionDef | undefined;
  holder: TeamPositionEntryPublic | undefined;
  playersForPosition: { entry: TeamPositionEntryPublic; isPrimary: boolean }[];
  activeEntry: TeamPositionEntryPublic | null;
  activePlayerId: string | null;
  highlightedPlayerId?: string | null;
  canEdit: boolean;
  saving: boolean;
  onClick: () => void;
  onToggleCaptain?: (playerId: string) => void;
  onToggleVC?: (playerId: string) => void;
  onRemovePlayer?: (playerId: string) => void;
}

export function PositionCard({
  jerseyNumber,
  position,
  holder,
  playersForPosition,
  activeEntry,
  activePlayerId,
  highlightedPlayerId,
  canEdit,
  saving,
  onClick,
  onToggleCaptain,
  onToggleVC,
  onRemovePlayer,
}: PositionCardProps) {
  const isPrimaryOfActive = activeEntry?.jersey_number === jerseyNumber;
  const isSecondaryOfActive = activeEntry?.covers_positions.includes(jerseyNumber) ?? false;
  const hasActivePlayer = activePlayerId !== null;
  const isHighlighted = !!(holder && highlightedPlayerId === holder.player_id);
  const isClickable = canEdit && !saving;

  const posLabel = position?.abbreviation ?? `#${jerseyNumber}`;

  // 184: an occupied position is a dnd-kit draggable (works on touch). The hook
  // must run before the empty-card early return; it's `disabled` when there's no
  // holder. PointerSensor's activation distance lets a plain tap still fire
  // onClick (select), so drag and tap-to-select coexist on the same element.
  const dragLabel = holder ? (holder.player_name.split(" ").pop()?.toUpperCase() ?? "") : "";
  const drag = useDraggable({
    id: `position-slot-${jerseyNumber}`,
    data: { kind: "position-player", playerId: holder?.player_id ?? "", fromPosition: jerseyNumber, label: dragLabel },
    disabled: !holder || !canEdit || saving,
  });

  // Reserves covering this position (even when no primary holder)
  const coveringReserves = playersForPosition.filter((p) => !p.isPrimary);

  // Empty position -- no player assigned as primary
  if (!holder) {
    let borderClass = "border-muted-foreground/40";
    if (isPrimaryOfActive) {
      borderClass = "border-primary ring-2 ring-primary/30";
    } else if (isSecondaryOfActive) {
      borderClass = "border-primary/60";
    } else if (hasActivePlayer) {
      borderClass = "border-muted-foreground/40 hover:border-primary/60 hover:bg-primary/5";
    } else if (coveringReserves.length > 0) {
      borderClass = "border-amber-500/60";
    }

    return (
      <button
        type="button"
        onClick={isClickable ? onClick : undefined}
        disabled={!isClickable}
        className={`flex w-[7.5rem] flex-col items-center gap-0.5 rounded-lg border-2 border-dashed px-2 py-2 text-center transition-colors ${borderClass} ${
          isClickable && hasActivePlayer ? "cursor-pointer" : ""
        }`}
      >
        <span className="text-base font-bold text-muted-foreground">{jerseyNumber}</span>
        <span className="text-[11px] font-medium text-muted-foreground">{posLabel}</span>
        {coveringReserves.length > 0 && (
          <div className="flex flex-col gap-px w-full mt-0.5">
            {coveringReserves.map(({ entry }) => {
              const surname = entry.player_name.split(" ").pop()?.toUpperCase() ?? "";
              return (
                <span
                  key={entry.player_id}
                  className="text-[10px] text-amber-600 dark:text-amber-400 italic truncate max-w-full leading-tight"
                >
                  {surname}
                </span>
              );
            })}
          </div>
        )}
      </button>
    );
  }

  // Occupied position -- player assigned
  let borderClass = "border-border";
  let bgClass = "bg-background";
  if (isHighlighted) {
    borderClass = "border-primary ring-2 ring-primary/40";
    bgClass = "bg-primary/10";
  } else if (isPrimaryOfActive) {
    borderClass = "border-primary ring-2 ring-primary/30";
    bgClass = "bg-primary/10";
  } else if (isSecondaryOfActive) {
    borderClass = "border-primary/60 border-dashed";
    bgClass = "bg-primary/5";
  }

  const primaryPlayer = playersForPosition.find((p) => p.isPrimary);
  const playerId = holder.player_id;

  return (
    <div
      ref={drag.setNodeRef}
      {...drag.listeners}
      {...drag.attributes}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={(e) => { if (e.key === "Enter" && isClickable) onClick(); }}
      className={`flex w-[7.5rem] flex-col items-center gap-0.5 rounded-lg border-2 px-2 py-1.5 text-center transition-colors shadow-sm ${borderClass} ${bgClass} ${
        isClickable ? "cursor-pointer hover:shadow-md" : ""
      } ${drag.isDragging ? "opacity-40" : ""}`}
    >
      {/* Jersey number + captain/VC badges */}
      <div className="flex items-center gap-1">
        <span className="text-base font-bold">{jerseyNumber}</span>
        {primaryPlayer?.entry.is_captain && (
          <span className="rounded bg-amber-500 px-1 py-px text-[9px] font-bold text-white leading-tight">
            C
          </span>
        )}
        {primaryPlayer?.entry.is_vice_captain && (
          <span className="rounded bg-blue-500 px-1 py-px text-[9px] font-bold text-white leading-tight">
            VC
          </span>
        )}
      </div>

      {/* Position abbreviation */}
      <span className="text-[10px] text-muted-foreground">{posLabel}</span>

      {/* Player names */}
      {playersForPosition.length > 0 && (
        <div className="flex flex-col gap-px w-full mt-0.5">
          {playersForPosition.map(({ entry, isPrimary }) => {
            const surname = entry.player_name.split(" ").pop()?.toUpperCase() ?? "";
            return (
              <span
                key={entry.player_id}
                className={`text-[11px] truncate max-w-full leading-tight ${
                  isPrimary ? "font-bold" : "text-muted-foreground italic text-[10px]"
                }`}
              >
                {surname}
              </span>
            );
          })}
        </div>
      )}

      {/* Action buttons -- shown when card is selected/highlighted */}
      {isHighlighted && canEdit && (
        <div className="flex items-center gap-1 mt-1 pt-1 border-t border-border/50 w-full justify-center">
          <button
            type="button"
            title={primaryPlayer?.entry.is_captain ? "Remove captain" : "Make captain"}
            disabled={saving}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCaptain?.(playerId);
            }}
            className={`rounded p-0.5 transition-colors ${
              primaryPlayer?.entry.is_captain
                ? "bg-amber-500/20 text-amber-600"
                : "hover:bg-muted text-muted-foreground"
            }`}
          >
            <Shield className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title={primaryPlayer?.entry.is_vice_captain ? "Remove vice-captain" : "Make vice-captain"}
            disabled={saving}
            onClick={(e) => {
              e.stopPropagation();
              onToggleVC?.(playerId);
            }}
            className={`rounded p-0.5 transition-colors ${
              primaryPlayer?.entry.is_vice_captain
                ? "bg-blue-500/20 text-blue-600"
                : "hover:bg-muted text-muted-foreground"
            }`}
          >
            <Star className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title="Remove from team"
            disabled={saving}
            onClick={(e) => {
              e.stopPropagation();
              onRemovePlayer?.(playerId);
            }}
            className="rounded p-0.5 hover:bg-destructive/20 text-destructive transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
